import { createReducer } from 'redux-starter-kit';
import { fetchSignOut, fetchSessionUser, setSessionUser } from './sessionUiActions';
import { selectRequest } from '../../domain/requests/RequestReducer';
import { selectUser } from '../../domain/users/userReducer';


/**
 * A selector for the current session user.
 * @param {object} state The redux state for the application.
 * @example const user = selectSessionUser(state);
 */
export const selectSessionUser = (state) => {
  const { userId } = state.via.ui.application.session;
  if (!userId) return null;

  return selectUser(state, userId);
};

/**
 * A selector for roles linked to the current session user.
 * @param {object} state The redux state for the application.
 */
export const selectSessionUserRoles = (state) => {
  const user = selectSessionUserRoles(state);
  if (!user || !user.linkedRoles) return [];

  return user.linkedRoles;
};

/**
 * A selector that checks if the current session user has the inspector role.
 * @param {object} state The redux state for the application.
 */
export const selectUserHasInspectorRole = (state) => {
  const roles = selectSessionUserRoles(state);

  return roles.includes('inspector');
};

/**
 * A selector that checks if the current session user has the group administrator role.
 * @param {object} state The redux state for the application.
 */
export const selectUserHasGroupAdminRole = (state) => {
  const roles = selectSessionUserRoles(state);

  return roles.includes('group_administrator');
};

/**
 * A selector for the current initialisation state.
 * True if the application has checked if the session is still active on first load.
 * @param {object} state The redux state for the application
 */
export const selectIsInitialised = state => state.via.ui.application.session.isInitialised;

/**
 * A selector for the current session user request state.
 * @param {object} state The redux state for the application.
 */
export const selectSessionUserRequest = (state) => {
  const requestId = state.via.ui.application.session.sessionUserRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};


const initialSessionState = {
  isInitialised: false, // Has the session been checked against the server?
  userId: null, // The user of the signed in session. Null if not signed in.
  signOutRequestId: null, // The id of the current signout request.
  sessionUserRequestId: null, // The id of the request for the current sessionUser.
};

/**
 * A reducer for the application session state.
 */
const sessionReducer = createReducer(initialSessionState, {
  // The session has been determined.
  [setSessionUser]: (state, action) => ({
    ...state,
    isInitialised: true,
    userId: action.payload,
  }),

  // A signout request has been made.
  [fetchSignOut]: (state, action) => ({
    ...state,
    signOutRequestId: action.payload,
  }),

  // A request for the current session user has been made.
  [fetchSessionUser]: (state, action) => {
    return ({
      ...state,
      sessionUserRequestId: action.payload,
    });
  },
});

export default sessionReducer;
